import { lazy, Suspense, ReactElement } from 'react';
import { Box } from '@mui/material';

import MainLayout from './MainLayout';
import MainLayoutDashboard from './MainLayoutDashboard';
import AuthGuard from 'utils/route-guard/AuthGuard';
import GuestGuard from 'utils/route-guard/GuestGuard';
import Loader from 'components/Loader';

const Header = lazy(() => import('./Header'));
const HeaderMain = lazy(() => import('./MainLayout/Header'));
const FooterBlock = lazy(() => import('./FooterBlock'));

// ==============================|| LAYOUTS - STRUCTURE ||============================== //

interface Props {
  children: ReactElement;
  variant?: 'main' | 'blank' | 'landing' | 'landing-auth' | 'auth-fullpage' | 'simple' | 'component' | 'auth' | 'dashboard';
}

export default function Layout({ variant = 'main', children }: Props) {
  if (variant === 'landing' || variant === 'simple') {
    return (
      <Suspense fallback={<Loader />}>
        <Header layout={variant} />
        {children}
        <FooterBlock isFull={variant === 'landing'} />
      </Suspense>
    );
  }

  if (variant === 'landing-auth') {
    return (
      <AuthGuard>
        <Suspense fallback={<Loader />}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              height: '100vh',
              maxHeight: '100vh',
              overflow: 'hidden'
            }}
          >
            <HeaderMain open={false} variant={variant} />
            <Box
              component="main"
              sx={{
                flex: 1,
                marginTop: '64px',
                minHeight: 0,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                '& > *': { flex: 1, minHeight: 0, overflow: 'hidden' }
              }}
            >
              {children}
            </Box>
          </Box>
        </Suspense>
      </AuthGuard>
    );
  }

  if (variant === 'auth-fullpage') {
    return (
      <AuthGuard>
        <Suspense fallback={<Loader />}>{children}</Suspense>
      </AuthGuard>
    );
  }

  if (variant === 'blank') {
    return children;
  }

  if (variant === 'auth') {
    return <GuestGuard>{children}</GuestGuard>;
  }

  if (variant === 'dashboard') {
    return (
      <AuthGuard>
      <MainLayoutDashboard>{children}</MainLayoutDashboard>
    </AuthGuard>
    );
  }

  return (
    <AuthGuard>
      <MainLayout>{children}</MainLayout>
    </AuthGuard>
  );
}
